import React, { useState, useContext, useEffect } from 'react';
import {
  Modal,
  Box,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Link,
  Popper,
} from '@mui/material';
import { GlobalContext } from '../../contexts/GlobalContext';
import PlayAgainButton from '../PlayAgainButton/PlayAgainButton';

const boxWidth = '345px';

const GuessBox = () => {
  const { globalContext, setAnsweredCorrectly, setMaxGuessesUsed } =
    useContext(GlobalContext);

  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [hint, setHint] = useState(''); // Add hint state
  const maxGuesses = 5;

  useEffect(() => {
    if (guesses.length >= maxGuesses) {
      setMaxGuessesUsed(true);
    }
  }, [guesses, setMaxGuessesUsed]);

  const clubNames = [
    'A.F.C. Bournemouth',
    'Arsenal F.C.',
    'Aston Villa',
    'Brentford',
    'Brighton & Hove Albion',
    'Chelsea F.C.',
    'Crystal Palace F.C.',
    'Everton F.C.',
    'Fulham F.C.',
    'Ipswich Town F.C.',
    'Leicester City F.C.',
    'Liverpool F.C.',
    'Manchester City F.C.',
    'Manchester United F.C.',
    'Newcastle United F.C.',
    'Nottingham Forest F.C.',
    'Southampton F.C.',
    'Tottenham Hotspur F.C.',
    'West Ham United F.C.',
    'Wolverhampton Wanderers',
  ];

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleSelectionChange = (event, newValue) => {
    setSelectedValue(newValue);
    setInputValue(newValue);
  };

  const handleKeyDown = (event) => {
    if (
      event.type === 'click' ||
      (event.key === 'Enter' && selectedValue && guesses.length < maxGuesses)
    ) {
      const isCorrect =
        globalContext.currentCrest.clubName.trim().toLowerCase() ===
        selectedValue.trim().toLowerCase();

      setGuesses((prevGuesses) => [
        { text: selectedValue, isCorrect: isCorrect },
        ...prevGuesses,
      ]);

      if (isCorrect) {
        setGameOver(true);
        setOpenModal(true);
        setAnsweredCorrectly(true);
        setHint(''); // Clear hint on correct answer
      } else {
        // Generate a hint when the guess is incorrect
        setHint(
          `Hint: The team is from ${globalContext.currentCrest.location}`,
        );
      }

      setSelectedValue('');
      setInputValue('');
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="correct-answer-modal"
        aria-describedby="correct-answer-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography id="correct-answer-modal" variant="h3" component="h1">
            Correct answer!
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img
              src={globalContext.currentCrest.imageUrl}
              alt={globalContext.currentCrest.clubName}
              style={{ maxHeight: '150px', maxWidth: '100%' }}
            />
          </Box>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Team name:{' '}
            <Link
              href={globalContext.currentCrest.wikipediaLink}
              target="_blank"
            >
              {globalContext.currentCrest.clubName}
            </Link>
          </Typography>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Location: {globalContext.currentCrest.location}
          </Typography>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Founded: {globalContext.currentCrest.yearFounded}
          </Typography>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Notable players: {globalContext.currentCrest.notablePlayers}
          </Typography>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Nickname: {globalContext.currentCrest.nickname}
          </Typography>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Main colors: {globalContext.currentCrest.mainColors}
          </Typography>

          <Button
            onClick={handleCloseModal}
            sx={{ mt: 2 }}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </Box>
      </Modal>
      {gameOver && guesses.length <= maxGuesses ? (
        <>
          <Typography id="correct-answer-modal" variant="h7" component="h3">
            Correct answer!
          </Typography>
          <PlayAgainButton />
        </>
      ) : (
        <>
          <Autocomplete
            freeSolo
            disableClearable
            options={clubNames}
            value={inputValue}
            onInputChange={handleInputChange}
            onChange={handleSelectionChange}
            renderInput={(params) => (
              <TextField
                {...params}
                id="outlined-basic"
                color="secondary"
                placeholder="Enter the name of a football club"
                focused
                variant="outlined"
                sx={{
                  input: { color: 'white' },
                  backgroundColor: 'black',
                  width: boxWidth,
                  marginTop: '20px',
                  display: guesses.length >= maxGuesses ? 'none' : '',
                }}
                onKeyDown={handleKeyDown}
                disabled={guesses.length >= maxGuesses}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleKeyDown}
                    >
                      Submit
                    </Button>
                  ),
                }}
              />
            )}
            PopperComponent={(props) => (
              <Popper
                {...props}
                sx={{
                  zIndex: 9999,
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  width: boxWidth,
                }}
              />
            )}
          />
          {hint && (
            <Box
              sx={{
                backgroundColor: '#ffe0b2',
                padding: '10px',
                borderRadius: '8px',
                marginTop: 2,
                textAlign: 'center',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Typography variant="body2" color="primary">
                {hint}
              </Typography>
            </Box>
          )}
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: 2,
        }}
      >
        {guesses.length >= maxGuesses && (
          <>
            <Typography
              variant="body3"
              color="black"
              sx={{ marginTop: 2, fontWeight: 'bold' }}
            >
              You have reached the maximum number of guesses.
            </Typography>
            <PlayAgainButton />
          </>
        )}
        {guesses.map((guess, index) => (
          <Box
            key={index}
            sx={{
              marginTop: 2,
              padding: 2,
              border: '1px solid #ccc',
              backgroundColor: guess.isCorrect ? '#28a745' : '#cf0c0c',
              color: 'white',
              width: boxWidth,
              textAlign: 'center',
              borderRadius: 1,
            }}
          >
            <Typography variant="body1">{guess.text}</Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default GuessBox;
